import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useSubmissionsCounter } from "@/components/AdminLayout";
import {
  Search, Download, X, User, CheckCircle, XCircle, Copy, Archive,
  Inbox, Loader2, FileText, ExternalLink, AlertTriangle, MessageSquarePlus,
  RefreshCw, Send, Eye,
} from "lucide-react";

const STATUS_META = {
  new: { label: "New", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  under_review: { label: "Under Review", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  more_info_requested: { label: "More Info", cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  approved: { label: "Approved", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  rejected: { label: "Rejected", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  duplicate: { label: "Duplicate", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  converted: { label: "Converted", cls: "bg-teal-500/10 text-teal-400 border-teal-500/20" },
  archived: { label: "Archived", cls: "bg-slate-600/20 text-slate-400 border-slate-600/30" },
};

const FILTER_TABS = ["all", "new", "under_review", "more_info_requested", "approved", "rejected", "duplicate", "converted", "archived"];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={cn("px-2 py-0.5 rounded border text-[11px] font-bold whitespace-nowrap", m.cls)}>{m.label}</span>;
}

function resolveUrl(u) {
  if (!u) return "";
  return u.startsWith("http") ? u : u;
}

export default function ChannelSubmissions() {
  const [data, setData] = useState({ submissions: [], total: 0, counts: {}, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [assigned, setAssigned] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState({ isOpen: false });
  const [toast, setToast] = useState("");
  const { refreshCounter } = useSubmissionsCounter();

  const notify = (m) => { setToast(m); setTimeout(() => setToast(""), 3000); };

  const fetchList = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 25 });
    if (search) params.append("search", search);
    if (status !== "all") params.append("status", status);
    if (assigned) params.append("assigned", assigned);
    axios.get(`${API}/admin/channel-submissions?${params}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, status, assigned]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (id) => {
    setSelected(id); setDetail(null);
    const r = await axios.get(`${API}/admin/channel-submissions/${id}`, { withCredentials: true });
    setDetail(r.data);
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); notify(msg); if (selected) await openDetail(selected); fetchList(); refreshCounter(); }
    catch (e) { notify(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const assignSelf = (id) => act(() => axios.post(`${API}/admin/channel-submissions/${id}/assign`, {}, { withCredentials: true }), "Assigned to you");
  const setStatusAction = (id, s) => act(() => axios.patch(`${API}/admin/channel-submissions/${id}/status`, { status: s }, { withCredentials: true }), `Marked ${s}`);
  const approve = (id) => act(() => axios.post(`${API}/admin/channel-submissions/${id}/approve`, {}, { withCredentials: true }), "Approved");
  const markDuplicate = (id) => act(() => axios.post(`${API}/admin/channel-submissions/${id}/mark-duplicate`, {}, { withCredentials: true }), "Marked duplicate");

  const reject = (id) => setConfirm({
    isOpen: true, title: "Reject Submission", message: "Optionally the submitter will be notified. Confirm rejection?",
    onConfirm: () => act(() => axios.post(`${API}/admin/channel-submissions/${id}/reject`, { reason: "" }, { withCredentials: true }), "Rejected"),
  });

  const requestInfo = (id) => act(() => axios.post(`${API}/admin/channel-submissions/${id}/request-info`, { message: "Please provide more information about your channel." }, { withCredentials: true }), "Info requested");

  const convert = (id) => setConfirm({
    isOpen: true, title: "Convert to Channel Draft",
    message: "This creates an UNPUBLISHED draft channel pre-filled from the submission. Continue?",
    onConfirm: () => act(async () => {
      const r = await axios.post(`${API}/admin/channel-submissions/${id}/convert`, {}, { withCredentials: true });
      notify(`Draft channel created (${r.data.slug})`);
    }, "Converted to draft"),
  });

  const archive = (id) => act(() => axios.delete(`${API}/admin/channel-submissions/${id}`, { withCredentials: true }), "Archived");

  const addNote = async (id) => {
    if (!note.trim()) return;
    await axios.post(`${API}/admin/channel-submissions/${id}/notes`, { text: note }, { withCredentials: true });
    setNote(""); openDetail(id);
  };

  const exportCsv = () => { window.open(`${API}/admin/channel-submissions/export/csv`, "_blank"); };

  const counts = data.counts || {};

  return (
    <div data-testid="channel-submissions-page">
      {toast && <div className="fixed top-4 right-4 z-[200] bg-slate-800 border border-slate-700 text-white text-sm px-4 py-2.5 rounded-xl shadow-xl" data-testid="cs-toast">{toast}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {["new", "under_review", "approved", "converted", "rejected", "duplicate"].map((k) => (
          <div key={k} className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <div className="text-2xl font-bold text-white">{counts[k] || 0}</div>
            <div className="text-xs text-slate-500">{STATUS_META[k].label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input placeholder="Search channel, name or email..." data-testid="cs-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select value={assigned} data-testid="cs-assigned-filter" onChange={(e) => { setAssigned(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            <option value="">All Reviewers</option>
            <option value="me">Assigned to Me</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <button onClick={fetchList} data-testid="cs-refresh" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={exportCsv} data-testid="cs-export" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors whitespace-nowrap">
            <Download size={16} /> Export CSV
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTER_TABS.map((tab) => (
            <button key={tab} data-testid={`cs-tab-${tab}`} onClick={() => { setStatus(tab); setPage(1); }}
              className={cn("px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border",
                status === tab ? "bg-blue-600 text-white border-blue-500" : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white")}>
              {tab === "all" ? "All" : STATUS_META[tab].label}
              {tab !== "all" && counts[tab] ? ` (${counts[tab]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="cs-table">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800">
                {["Channel", "Submitter", "Country", "Category", "Relationship", "Date", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500"><Loader2 className="animate-spin mx-auto" /></td></tr>
              ) : data.submissions.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center">
                  <Inbox className="mx-auto text-slate-700 mb-3" size={40} />
                  <p className="text-slate-500">No submissions found</p>
                </td></tr>
              ) : data.submissions.map((s) => (
                <tr key={s.id} className="hover:bg-slate-800/40 transition-colors cursor-pointer" data-testid={`cs-row-${s.id}`} onClick={() => openDetail(s.id)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white text-sm">{s.channel_name}</span>
                      {s.is_possible_duplicate && <AlertTriangle size={14} className="text-orange-400" title="Possible duplicate" data-testid={`cs-dup-${s.id}`} />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">{s.submitter_name}<div className="text-xs text-slate-500">{s.submitter_email}</div></td>
                  <td className="px-4 py-3 text-sm text-slate-400">{s.country_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{s.category_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-400 capitalize">{s.relationship}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button className="p-2 text-slate-400 hover:text-blue-400" data-testid={`cs-view-${s.id}`}><Eye size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.pages > 1 && (
          <div className="p-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/50">
            <span className="text-sm text-slate-400">Page {page} / {data.pages} • {data.total} total</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 rounded-lg border border-slate-800 bg-slate-900 text-slate-400 hover:text-white disabled:opacity-40">Prev</button>
              <button disabled={page === data.pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded-lg border border-slate-800 bg-slate-900 text-slate-400 hover:text-white disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-slate-950/70 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl bg-slate-900 h-full overflow-y-auto border-l border-slate-800 animate-slideLeft" onClick={(e) => e.stopPropagation()} data-testid="cs-detail">
            {!detail ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-slate-500" /></div>
            ) : (
              <>
                <div className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 p-5 flex items-center justify-between z-10">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg text-white">{detail.channel_name}</h3>
                      <StatusBadge status={detail.status} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{detail.assigned_to_name ? `Assigned to ${detail.assigned_to_name}` : "Unassigned"}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white" data-testid="cs-detail-close"><X size={22} /></button>
                </div>

                <div className="p-5 space-y-5">
                  {detail.is_possible_duplicate && (
                    <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 px-4 py-3 flex items-start gap-2" data-testid="cs-dup-warning">
                      <AlertTriangle size={16} className="text-orange-400 mt-0.5" />
                      <div className="text-sm text-orange-300">
                        Possible duplicate — flagged for review.
                        <div className="text-xs text-orange-400/70 mt-1">{(detail.duplicate_reasons || []).join(", ")}</div>
                      </div>
                    </div>
                  )}

                  {/* Action bar */}
                  <div className="flex flex-wrap gap-2">
                    <button disabled={busy} onClick={() => assignSelf(detail.id)} data-testid="cs-assign-self" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-xs font-medium hover:bg-slate-700"><User size={14} /> Assign to me</button>
                    <button disabled={busy} onClick={() => setStatusAction(detail.id, "under_review")} data-testid="cs-under-review" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-400 text-xs font-medium hover:bg-amber-500/20"><Eye size={14} /> Under Review</button>
                    <button disabled={busy} onClick={() => requestInfo(detail.id)} data-testid="cs-request-info" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/10 text-purple-400 text-xs font-medium hover:bg-purple-500/20"><Send size={14} /> Request Info</button>
                    <button disabled={busy} onClick={() => approve(detail.id)} data-testid="cs-approve" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium hover:bg-emerald-500/20"><CheckCircle size={14} /> Approve</button>
                    <button disabled={busy} onClick={() => reject(detail.id)} data-testid="cs-reject" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20"><XCircle size={14} /> Reject</button>
                    <button disabled={busy} onClick={() => markDuplicate(detail.id)} data-testid="cs-mark-duplicate" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-500/10 text-orange-400 text-xs font-medium hover:bg-orange-500/20"><Copy size={14} /> Duplicate</button>
                    <button disabled={busy} onClick={() => convert(detail.id)} data-testid="cs-convert" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-xs font-bold hover:bg-teal-500"><Copy size={14} /> Convert to Draft</button>
                    <button disabled={busy} onClick={() => archive(detail.id)} data-testid="cs-archive" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-slate-400 text-xs font-medium hover:bg-slate-700"><Archive size={14} /> Archive</button>
                  </div>

                  {detail.converted_channel_id && (
                    <div className="rounded-xl border border-teal-500/20 bg-teal-500/5 px-4 py-2.5 text-sm text-teal-300">Converted to draft channel. Review it in Channels before publishing.</div>
                  )}

                  {/* Fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    {[
                      ["Submitter", detail.submitter_name],
                      ["Email", detail.submitter_email],
                      ["Phone", detail.submitter_phone],
                      ["Relationship", detail.relationship],
                      ["Country", detail.country_name],
                      ["Category", detail.category_name],
                      ["Language", detail.primary_language],
                    ].map(([k, v]) => (
                      <div key={k}><div className="text-xs text-slate-500">{k}</div><div className="text-slate-200">{v || "—"}</div></div>
                    ))}
                  </div>

                  {detail.channel_website && (
                    <a href={resolveUrl(detail.channel_website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:underline"><ExternalLink size={14} /> {detail.channel_website}</a>
                  )}

                  {(detail.description_en || detail.description_fr) && (
                    <div><div className="text-xs text-slate-500 mb-1">Description</div><p className="text-sm text-slate-300">{detail.description_en || detail.description_fr}</p></div>
                  )}

                  {/* Stream URLs (display only) */}
                  <div className="space-y-2">
                    <div className="text-xs text-slate-500">Stream URLs</div>
                    <code className="block text-xs text-slate-300 bg-slate-950 rounded-lg px-3 py-2 break-all">{detail.stream_url || "—"}</code>
                    {detail.backup_stream_url && <code className="block text-xs text-slate-400 bg-slate-950 rounded-lg px-3 py-2 break-all">{detail.backup_stream_url}</code>}
                  </div>

                  {/* Files */}
                  <div className="flex flex-wrap gap-3">
                    {detail.logo_url && <a href={resolveUrl(detail.logo_url)} target="_blank" rel="noreferrer" data-testid="cs-file-logo"><img src={resolveUrl(detail.logo_url)} alt="logo" className="w-16 h-16 rounded-lg object-cover border border-slate-700" referrerPolicy="no-referrer" /></a>}
                    {detail.cover_url && <a href={resolveUrl(detail.cover_url)} target="_blank" rel="noreferrer"><img src={resolveUrl(detail.cover_url)} alt="cover" className="w-28 h-16 rounded-lg object-cover border border-slate-700" referrerPolicy="no-referrer" /></a>}
                    {detail.document_url && <a href={resolveUrl(detail.document_url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-xs"><FileText size={16} /> View document</a>}
                  </div>

                  {detail.additional_notes && (
                    <div><div className="text-xs text-slate-500 mb-1">Additional notes from submitter</div><p className="text-sm text-slate-300">{detail.additional_notes}</p></div>
                  )}

                  {/* Internal notes */}
                  <div className="border-t border-slate-800 pt-4">
                    <h4 className="text-sm font-semibold text-white mb-3">Internal Notes</h4>
                    <div className="space-y-2 mb-3">
                      {(detail.notes || []).length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
                      {(detail.notes || []).map((n) => (
                        <div key={n.id} className="rounded-lg bg-slate-950 px-3 py-2">
                          <div className="text-sm text-slate-200">{n.text}</div>
                          <div className="text-[11px] text-slate-500 mt-1">{n.author_name} • {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note..." data-testid="cs-note-input"
                        className="flex-grow px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                      <button onClick={() => addNote(detail.id)} data-testid="cs-note-add" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500"><MessageSquarePlus size={14} /> Add</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmModal isOpen={confirm.isOpen} title={confirm.title} message={confirm.message}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ isOpen: false }); }}
        onCancel={() => setConfirm({ isOpen: false })} />
    </div>
  );
}
